import {Button, Card, Col, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, ReloadOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import {useEffect, useState} from "react";
import flexCurrency from "../../utils/FlexCurrency";

const tableStyle = `
  .fk-rapor-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #fk-rapor-print-area, #fk-rapor-print-area * { visibility: visible; }
    #fk-rapor-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const GISE_HEX = {1: "#1677ff", 2: "#722ed1"};

const columns = [
    {
        title: "Tarih",
        dataIndex: "date",
        render: d => new Date(d).toLocaleDateString("tr-TR"),
    },
    {
        title: "Çekim",
        dataIndex: "shots",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: "Satış",
        dataIndex: "sales",
        className: "text-align-right",
        render: v => v.toLocaleString("tr-TR"),
    },
    {
        title: <span style={{color: GISE_HEX[1]}}>1. Gişe Tutar (₺)</span>,
        dataIndex: "amount1",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[1]}}>{flexCurrency(v)}</b>,
    },
    {
        title: <span style={{color: GISE_HEX[2]}}>2. Gişe Tutar (₺)</span>,
        dataIndex: "amount2",
        className: "text-align-right",
        render: v => <b style={{color: GISE_HEX[2]}}>{flexCurrency(v)}</b>,
    },
    {
        title: "Toplam Tutar (₺)",
        className: "text-align-right",
        render: r => <b style={{color: "#52c41a"}}>{flexCurrency(r.amount1 + r.amount2)}</b>,
    },
];

const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const groupByYearMonth = (data) => {
    const groups = {};
    data.forEach(item => {
        const yil = item.date.slice(0, 4);
        const ay  = item.date.slice(0, 7);
        if (!groups[yil])     groups[yil] = {};
        if (!groups[yil][ay]) groups[yil][ay] = [];
        groups[yil][ay].push(item);
    });

    return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(yil => {
            const aylar = Object.keys(groups[yil])
                .sort((a, b) => b.localeCompare(a))
                .map(ay => {
                    const items = groups[yil][ay];

                    const uniqueDates = [...new Set(items.map(i => i.date))]
                        .sort((a, b) => new Date(b) - new Date(a));

                    const tableRows = uniqueDates.map(date => {
                        const s1 = items.find(i => i.date === date && i.station_id === 1);
                        const s2 = items.find(i => i.date === date && i.station_id === 2);
                        return {
                            date,
                            shots:   (s1 ?? s2)?.shots ?? 0,
                            sales:   (s1 ?? s2)?.sales ?? 0,
                            amount1: s1?.amount ?? 0,
                            amount2: s2?.amount ?? 0,
                        };
                    });

                    const uniqueDateItems = Object.values(
                        items.reduce((acc, i) => { if (!acc[i.date]) acc[i.date] = i; return acc; }, {})
                    );
                    const total = {
                        shots:   uniqueDateItems.reduce((s, i) => s + i.shots,  0),
                        sales:   uniqueDateItems.reduce((s, i) => s + i.sales,  0),
                        amount1: tableRows.reduce((s, r) => s + r.amount1, 0),
                        amount2: tableRows.reduce((s, r) => s + r.amount2, 0),
                        amount:  items.reduce((s, i) => s + i.amount, 0),
                    };

                    const ayNo  = parseInt(ay.slice(5), 10) - 1;
                    const label = `${AY_ADLARI[ayNo]} ${yil}`;
                    return {ay, label, tableRows, total};
                });

            // Yıllık toplamlar (ay toplamlarının toplamı; shots/sales ay bazında zaten tekilleştirildi)
            const yilToplam = aylar.reduce((acc, {total}) => ({
                shots:   acc.shots   + total.shots,
                sales:   acc.sales   + total.sales,
                amount1: acc.amount1 + total.amount1,
                amount2: acc.amount2 + total.amount2,
                amount:  acc.amount  + total.amount,
            }), {shots: 0, sales: 0, amount1: 0, amount2: 0, amount: 0});

            return {yil, aylar, yilToplam};
        });
};

/* ── Ay özet satırı ─────────────────────────────────────── */
const AyOzet = ({total}) => (
    <Row
        gutter={[16, 8]}
        style={{padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0"}}
    >
        <Col xs={12} sm={4}>
            <Statistic
                title="Toplam Çekim"
                value={total.shots}
                valueStyle={{fontSize: 13}}
                formatter={v => v.toLocaleString("tr-TR")}
            />
        </Col>
        <Col xs={12} sm={4}>
            <Statistic
                title="Toplam Satış"
                value={total.sales}
                valueStyle={{fontSize: 13}}
                formatter={v => v.toLocaleString("tr-TR")}
            />
        </Col>
        <Col xs={12} sm={5}>
            <Statistic
                title="1. Gişe"
                value={total.amount1}
                suffix="₺"
                valueStyle={{fontSize: 13, color: GISE_HEX[1]}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={5}>
            <Statistic
                title="2. Gişe"
                value={total.amount2}
                suffix="₺"
                valueStyle={{fontSize: 13, color: GISE_HEX[2]}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={24} sm={6}>
            <Statistic
                title="Toplam Tutar"
                value={total.amount}
                suffix="₺"
                valueStyle={{fontSize: 14, color: "#52c41a", fontWeight: 700}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
    </Row>
);

/* ── Yıl özet kartı ─────────────────────────────────────── */
const YilOzet = ({yil, yilToplam}) => (
    <Card
        style={{
            marginBottom: 12,
            background: "linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 100%)",
            border: "1px solid #91caff",
        }}
        bodyStyle={{padding: "12px 16px"}}
    >
        <Row align="middle" gutter={[24, 8]}>
            <Col xs={24} sm={4}>
                <div style={{fontSize: 20, fontWeight: 800, color: "#1677ff", letterSpacing: 1}}>
                    {yil}
                </div>
                <div style={{fontSize: 11, color: "#888", marginTop: 2}}>Yıllık Özet</div>
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="Toplam Çekim"
                    value={yilToplam.shots}
                    valueStyle={{fontSize: 14, fontWeight: 600}}
                    formatter={v => v.toLocaleString("tr-TR")}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="Toplam Satış"
                    value={yilToplam.sales}
                    valueStyle={{fontSize: 14, fontWeight: 600}}
                    formatter={v => v.toLocaleString("tr-TR")}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="1. Gişe Tutar"
                    value={yilToplam.amount1}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: GISE_HEX[1], fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={4}>
                <Statistic
                    title="2. Gişe Tutar"
                    value={yilToplam.amount2}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: GISE_HEX[2], fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={24} sm={4}>
                <Statistic
                    title="Genel Toplam"
                    value={yilToplam.amount}
                    suffix="₺"
                    valueStyle={{fontSize: 16, color: "#52c41a", fontWeight: 800}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
        </Row>
    </Card>
);

/* ── Ana bileşen ────────────────────────────────────────── */
const FotoKapanRaporPage = () => {
    const [yearData, setYearData] = useState([]);
    const [loading, setLoading]   = useState(false);

    useEffect(() => { getData(); }, []);

    const getData = () => {
        setLoading(true);
        Api.get("/api/FotoKapan/GetTotalKazanclar")
            .then(res => setYearData(groupByYearMonth(res.data)))
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    return (
        <div className={"p-1"}>
            <style>{tableStyle}</style>

            {/* Üst bar */}
            <Card
                className={"no-print"}
                style={{marginBottom: 12}}
                bodyStyle={{padding: "10px 16px"}}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <span style={{fontWeight: 700, fontSize: 15}}>Foto Kapan Yıllık Raporu</span>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={getData}
                            loading={loading}
                            style={{marginRight: 8}}
                        >
                            Yenile
                        </Button>
                        {yearData.length > 0 && (
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={() => window.print()}
                            >
                                Yazdır
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {loading && <Card loading style={{marginBottom: 12}} />}

            <div id={"fk-rapor-print-area"}>
            {yearData.map(({yil, aylar, yilToplam}) => (
                <div key={yil} style={{marginBottom: 24}}>
                    <YilOzet yil={yil} yilToplam={yilToplam} />

                    {aylar.map(({ay, label, tableRows, total}) => (
                        <Card
                            key={ay}
                            size={"small"}
                            title={<span style={{fontWeight: 600}}>{label}</span>}
                            bodyStyle={{padding: 0}}
                            style={{marginBottom: 8}}
                        >
                            <AyOzet total={total} />
                            <Table
                                className={"fk-rapor-table"}
                                size={"small"}
                                bordered
                                dataSource={tableRows}
                                rowKey={r => r.date}
                                columns={columns}
                                pagination={false}
                            />
                        </Card>
                    ))}
                </div>
            ))}
            </div>
        </div>
    );
};

export default FotoKapanRaporPage;
